import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { api } from 'src/services';
import dayjs from 'dayjs';

type GetCurrentPractitionerEntitlementsResponse = {
  features: Record<string, { isEntitled: boolean }>;
  is_plg_user: boolean;
  permissions: Record<string, unknown>;
  plan_expiry: string | null;
  plan_type: 'teams' | 'pro' | 'pro_trial' | 'free' | 'plg';
  plans: {
    assignOnSignup: boolean;
    createdAt: string;
    defaultTimeLimitation: unknown | null;
    defaultTreatment: 'false' | 'true';
    description: string | null;
    id: string;
    metadata: {
      tenant?: 'OP' | 'OpenDr';
      type?: 'pro' | 'teams' | 'plg' | 'enterprise';
      trial?: boolean;
    };
    name: string;
    rules?: unknown[];
    updatedAt?: string | null;
    vendorId: string;
  }[];
  request_note_preferences: boolean;
  roles: string[];
  show_onboarding_demo: boolean;
};

export const useGetCurrentPractitionerEntitlements = () => {
  const currentPractitionerEntitlements = useSelector<
    any,
    GetCurrentPractitionerEntitlementsResponse
  >((state) => state?.practitionerState.currentPractitionerEntitlements);

  const data = useQuery({
    queryKey: ['currentPractitionerEntitlements'],
    queryFn: () =>
      api
        .get<GetCurrentPractitionerEntitlementsResponse>('/self/entitlements')
        .then((res) => res.data),
  });

  const entitlements = data?.data;

  useEffect(() => {
    if (data.data) {
      data.refetch();
    }
  }, [currentPractitionerEntitlements]);

  const isAuraPro = entitlements?.plans?.some(
    (plan) => plan?.metadata?.type === 'pro'
  );
  const isAuraTeams = entitlements?.plans?.some(
    (plan) => plan?.metadata?.type === 'teams'
  );
  const isAuraEnterprise = entitlements?.plans?.some(
    (plan) => plan?.metadata?.type === 'enterprise'
  );
  const isPLGUser = entitlements?.plans?.some(
    (plan) => plan?.metadata?.type === 'plg'
  );
  const isAuraProTrial = entitlements?.plans?.some(
    (plan) => plan?.metadata?.trial === true
  );

  const auraProTrialPlanExpiry = entitlements?.plan_expiry;

  const auraProTrialRemainingDays = auraProTrialPlanExpiry
    ? dayjs(auraProTrialPlanExpiry).diff(dayjs(), 'day')
    : null;
  const auraProTrialRemainingSeconds = auraProTrialPlanExpiry
    ? dayjs(auraProTrialPlanExpiry).diff(dayjs(), 'second')
    : null;
  const isAuraProTrialExpired =
    isAuraProTrial && (auraProTrialRemainingSeconds ?? 1) <= 0;

  return {
    ...data,
    isAuraPro,
    isAuraTeams,
    isAuraEnterprise,
    isPLGUser,
    isAuraProTrial,
    isAuraProTrialExpired,
    auraProTrialRemainingDays,
    auraProTrialRemainingSeconds,
  };
};
